// FILE: components/explore/sections/ExamsSection.tsx
"use client";

import { memo } from "react";
import HardNavLink from "@/components/shared/HardNavLink";
import { Clock, BarChart2, ThumbsUp, Gem, Play } from "lucide-react";
import type { ExamItem } from "../_shared/types";

interface Props {
  items: ExamItem[];
  loading?: boolean;
}

function ExamCard({ item }: { item: ExamItem }) {
  const isFree = item.price === "free";
  const href = `/exams/${item.slug || item.id}`;
  const diffColor =
    item.difficulty === "Khó"
      ? "text-red-500 bg-red-50 border-red-200"
      : item.difficulty === "Dễ"
        ? "text-emerald-600 bg-emerald-50 border-emerald-200"
        : "text-amber-600 bg-amber-50 border-amber-200";

  return (
    <HardNavLink href={href}
      className="bg-white border border-border rounded-xl shadow-card hover:shadow-[0_8px_30px_rgba(0,0,0,.08)] hover:border-teal-500/30 transition-all cursor-pointer group block"
    >
      <div className="flex items-center gap-4 p-4 max-sm:flex-col max-sm:items-start">
        <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center text-2xl shrink-0 group-hover:scale-105 transition-transform">
          {item.emoji}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <h4 className="text-[14px] font-bold text-teal-800 truncate group-hover:text-orange-500 transition-colors">
              {item.title}
            </h4>
            {isFree && (
              <span className="inline-flex items-center px-2 py-[1px] rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0">
                FREE
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.tags.slice(0, 3).map((t, i) => (
              <span key={i} className="inline-flex items-center px-2 py-[1px] rounded-full text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
                {t}
              </span>
            ))}
            <span className={`inline-flex items-center px-2 py-[1px] rounded-full text-[9px] font-bold border ${diffColor}`}>
              {item.difficulty}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0 max-sm:w-full max-sm:justify-between max-sm:mt-2">
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <BarChart2 className="w-3.5 h-3.5" />{item.attempts.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />{item.time}
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-orange-500 text-white text-[11px] font-bold group-hover:bg-orange-600 transition-colors shadow-[0_2px_8px_rgba(249,115,22,.2)]">
            <Play className="w-3.5 h-3.5" />Làm bài
          </span>
        </div>
      </div>
    </HardNavLink>
  );
}

function ExamSkeleton() {
  return (
    <div className="bg-white border border-border rounded-xl shadow-card flex gap-4 p-4">
      <div className="w-12 h-12 rounded-xl bg-slate-100 shrink-0 animate-pulse" />
      <div className="flex-1 flex items-center justify-between gap-4">
        <div className="space-y-2 flex-1">
          <div className="h-4 w-2/3 bg-slate-100 rounded animate-pulse" />
          <div className="flex gap-1.5">
            <div className="h-3.5 w-14 bg-slate-100 rounded-full animate-pulse" />
            <div className="h-3.5 w-10 bg-slate-100 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="h-9 w-28 bg-slate-100 rounded-full shrink-0 animate-pulse" />
      </div>
    </div>
  );
}

function ExamsSectionImpl({ items, loading }: Props) {
  if (loading && items.length === 0) {
    return (
      <div className="flex flex-col gap-2.5">
        {[1, 2, 3, 4].map((i) => <ExamSkeleton key={i} />)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-[13px] text-muted-foreground">
        Chưa có đề thi nào
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {items.map((item) => (
        <ExamCard key={item.id} item={item} />
      ))}
    </div>
  );
}

export const ExamsSection = memo(ExamsSectionImpl);